import { SWITCH_ROUTES } from './routes';

const _urlParams = new URLSearchParams(window.location.search);
export const HAS_URL_UKEY = !!_urlParams.get('ukey');

// 由外部 App WebView 嵌入（URL 帶 ukey）。App 會在頂部浮出關閉/收合按鈕，
// 需要在頁面頂部留白讓這些按鈕落在空白區，避免蓋住 header。
export const IS_EMBEDDED_APP = HAS_URL_UKEY;

/**
 * 讀出 env(safe-area-inset-top) 的實際計算值（px）。
 * 純 CSS 的 calc(env(...) + 固定值) 在 App WebView 內不可靠：
 * WebView 若已被 App 定位在安全區之內，env() 會回傳 0，
 * 但 App 的浮動膠囊仍落在瀏海下方，導致留白不足、按鈕蓋住 header。
 * 因此改由 JS 實際量測，再依有無瀏海分別給值。
 */
function readSafeAreaInsetTop() {
    if (typeof document === 'undefined') return 0;
    try {
        const probe = document.createElement('div');
        probe.style.cssText =
            'position:fixed;top:0;left:0;visibility:hidden;pointer-events:none;height:env(safe-area-inset-top,0px)';
        document.documentElement.appendChild(probe);
        const value = probe.getBoundingClientRect().height;
        probe.remove();
        return Number.isFinite(value) ? value : 0;
    } catch (e) {
        return 0;
    }
}

// 有瀏海／動態島的機型，App 膠囊落在安全區下方，需要更多留白。
const EMBEDDED_TOP_OFFSET_NOTCH = 110;
// 無瀏海機型（多數 Android），膠囊貼齊頂端。
const EMBEDDED_TOP_OFFSET_PLAIN = 64;
// 判定為有瀏海的安全區門檻（px）。一般有瀏海機型為 44~59，無瀏海為 0~24。
const NOTCH_INSET_THRESHOLD = 30;

/**
 * App 內嵌時頁面頂部所需留白（px，含安全區）。
 * 非內嵌情境回傳 0，畫面與一般瀏覽完全一致。
 */
export function getEmbeddedTopOffset() {
    if (!IS_EMBEDDED_APP) return 0;
    const inset = readSafeAreaInsetTop();
    // env() 量到瀏海：安全區已被排除，只需再避開膠囊本身
    if (inset >= NOTCH_INSET_THRESHOLD) return inset + EMBEDDED_TOP_OFFSET_PLAIN;
    // env() 回 0：可能真的無瀏海，也可能是 WebView 已定位在安全區內而測不到。
    // 後者膠囊仍在瀏海下方，故用較大的固定值涵蓋兩種情況。
    return isLikelyNotchDevice() ? EMBEDDED_TOP_OFFSET_NOTCH : EMBEDDED_TOP_OFFSET_PLAIN;
}

/**
 * env() 測不到時的後備判斷：螢幕長邊/短邊比例 > 2 視為有瀏海。
 * iPhone X 之後約 2.16~2.22（之前為 1.78）；現代 Android 水滴/挖孔機
 * 型多為 2.1~2.3。Android WebView 常量不到 env(safe-area-inset-top)，
 * 需靠此後備涵蓋，才不會被 App 浮動膠囊蓋到內容。
 */
function isLikelyNotchDevice() {
    if (typeof window === 'undefined') return false;
    const { width, height } = window.screen || {};
    if (!width || !height) return false;
    const ratio = Math.max(width, height) / Math.min(width, height);
    return ratio > 2;
}

const GW_PROD_DOMAIN = 'gw123.net';
const GW_STG_DOMAIN = 'web-stg.gw123.net';

const AW_PROD_KH_DOMAIN = 'kh.aw123.cc';
const AW_PROD_MM_DOMAIN = 'mm.aw123.cc';
const AW_PROD_VN_DOMAIN = 'vn.aw123.cc';
const AW_PROD_BD_DOMAIN = 'bd.aw123.cc';
const AW_PROD_LA_DOMAIN = 'la.aw123.cc';
const AW_PROD_KE_DOMAIN = 'ke.aw123.cc';
const AW_PROD_NL_DOMAIN = 'nl.aw123.cc';
const AW_PROD_TT_DOMAIN = 'tt.aw123.cc';
const AW_PROD_MR_DOMAIN = 'mr.aw123.cc';
const AW_PROD_MY_DOMAIN = 'my.aw123.cc';
const AW_PROD_ID_DOMAIN = 'id.aw123.cc';

const ASIA_PROD_TT_DOMAIN = 'tt.acewin.asia';
const ASIA_PROD_MR_DOMAIN = 'mr.acewin.asia';
const ASIA_PROD_KE_DOMAIN = 'ke.acewin.asia';
const ASIA_PROD_VN_DOMAIN = 'vn.acewin.asia';
const ASIA_PROD_MY_DOMAIN = 'my.acewin.asia';
const ASIA_PROD_ID_DOMAIN = 'id.acewin.asia';



const BIZ_PROD_TT_DOMAIN = 'tt.acewin.biz';
const BIZ_PROD_LA_DOMAIN = 'la.acewin.biz';
const BIZ_PROD_KE_DOMAIN = 'ke.acewin.biz';
const BIZ_PROD_VN_DOMAIN = 'vn.acewin.biz';

const US_PROD_DOMAIN = 'acewin.us';
const ASIA_US_PROD_DOMAIN = 'u.acewin.asia';
const TGM_777_PROD_DOMAIN = 'tgm777.net';




const AW_STG_KH_DOMAIN = 'kh-stg.aw123.cc';
const AW_STG_MM_DOMAIN = 'mm-stg.aw123.cc';
const AW_STG_VN_DOMAIN = 'vn-stg.aw123.cc';
const AW_STG_BD_DOMAIN = 'bd-stg.aw123.cc';
const AW_STG_LA_DOMAIN = 'la-stg.aw123.cc';
const AW_STG_KE_DOMAIN = 'ke-stg.aw123.cc';
const AW_STG_NL_DOMAIN = 'nl-stg.aw123.cc';

const PECH_PROD_DOMAIN = 'pech.mobi';
const PECH_STG_DOMAIN = 'stg.pech.mobi';

const ACE_MM_PROD_DOMAIN = 'mm.acewin.asia';
const ACE_MM_STG_DOMAIN = 'mm-stg.acewin.asia';


const BITSBET_PROD_DOMAIN = 'bitsbet.co.ke';
const BITSBET_STG_DOMAIN = 'stg.bitsbet.co.ke';

const AW789_PROD_DOMAIN = 'aw789.site';
const CHEAP899_PROD_DOMAIN = 'cheap899.site';
const FISH123_PROD_DOMAIN = 'fish123.site';
const VN345_PROD_DOMAIN = 'vn345.site';
const XYZ567_PROD_DOMAIN = 'xyz567.store';


export const IS_GW_PORD = window.location.hostname === GW_PROD_DOMAIN;
export const IS_GW_STG = window.location.hostname === GW_STG_DOMAIN;
// export const IS_GW_STG = true;

export const IS_LOCAL = window.location.hostname === 'localhost';
// export const IS_LOCAL = true;

export const IS_GW = IS_GW_PORD || IS_GW_STG;

export const IS_BITSBET_PROD = window.location.hostname === BITSBET_PROD_DOMAIN;
export const IS_BITSBET_STG = window.location.hostname === BITSBET_STG_DOMAIN;

export const IS_PECH_PROD = window.location.hostname === PECH_PROD_DOMAIN;
export const IS_PECH_STG = window.location.hostname === PECH_STG_DOMAIN;
// export const IS_PECH_STG = true;

export const IS_ACE_MM_PROD = window.location.hostname === ACE_MM_PROD_DOMAIN;
export const IS_ACE_MM_STG = window.location.hostname === ACE_MM_STG_DOMAIN;


export const IS_AW_KH_PROD = window.location.hostname === AW_PROD_KH_DOMAIN;
export const IS_AW_MM_PROD = window.location.hostname === AW_PROD_MM_DOMAIN;
export const IS_AW_VN_PROD = window.location.hostname === AW_PROD_VN_DOMAIN
    || window.location.hostname === AW789_PROD_DOMAIN
    || window.location.hostname === CHEAP899_PROD_DOMAIN
    || window.location.hostname === FISH123_PROD_DOMAIN
    || window.location.hostname === VN345_PROD_DOMAIN
    || window.location.hostname === XYZ567_PROD_DOMAIN;
// export const IS_AW_VN_PROD = true;



export const IS_AW_BD_PROD = window.location.hostname === AW_PROD_BD_DOMAIN;
export const IS_AW_LA_PROD = window.location.hostname === AW_PROD_LA_DOMAIN;
export const IS_AW_KE_PROD = window.location.hostname === AW_PROD_KE_DOMAIN;
export const IS_AW_NL_PROD = window.location.hostname === AW_PROD_NL_DOMAIN;
export const IS_AW_TT_PROD = window.location.hostname === AW_PROD_TT_DOMAIN;
// export const IS_AW_TT_PROD = true;

export const IS_AW_MY_PROD = window.location.hostname === AW_PROD_MY_DOMAIN;
export const IS_AW_ID_PROD = window.location.hostname === AW_PROD_ID_DOMAIN;


export const IS_ASIA_TT_PROD = window.location.hostname === ASIA_PROD_TT_DOMAIN;
export const IS_ASIA_MR_PROD = window.location.hostname === ASIA_PROD_MR_DOMAIN;
export const IS_ASIA_KE_PROD = window.location.hostname === ASIA_PROD_KE_DOMAIN;
export const IS_ASIA_VN_PROD = window.location.hostname === ASIA_PROD_VN_DOMAIN;
export const IS_ASIA_MY_PROD = window.location.hostname === ASIA_PROD_MY_DOMAIN;
export const IS_ASIA_ID_PROD = window.location.hostname === ASIA_PROD_ID_DOMAIN;



 
export const IS_BIZ_TT_PROD = window.location.hostname === BIZ_PROD_TT_DOMAIN;
export const IS_BIZ_LA_PROD = window.location.hostname === BIZ_PROD_LA_DOMAIN;
export const IS_BIZ_KE_PROD = window.location.hostname === BIZ_PROD_KE_DOMAIN;
export const IS_BIZ_VN_PROD = window.location.hostname === BIZ_PROD_VN_DOMAIN;

export const IS_US_PROD = window.location.hostname === US_PROD_DOMAIN;
export const IS_ASIA_US_PROD = window.location.hostname === ASIA_US_PROD_DOMAIN;
export const IS_TGM777_PROD = window.location.hostname === TGM_777_PROD_DOMAIN;


export const IS_AW_MR_PROD = window.location.hostname === AW_PROD_MR_DOMAIN;


export const IS_AW_KH_STG = window.location.hostname === AW_STG_KH_DOMAIN;
// export const IS_AW_KH_STG = true;

export const IS_AW_MM_STG = window.location.hostname === AW_STG_MM_DOMAIN;
// export const IS_AW_MM_STG = true;

export const IS_AW_VN_STG = window.location.hostname === AW_STG_VN_DOMAIN;
// export const IS_AW_VN_STG = true;

export const IS_AW_BD_STG = window.location.hostname === AW_STG_BD_DOMAIN;
// export const IS_AW_BD_STG = true;

export const IS_AW_LA_STG = window.location.hostname === AW_STG_LA_DOMAIN;
// export const IS_AW_LA_STG = true;

export const IS_AW_KE_STG = window.location.hostname === AW_STG_KE_DOMAIN;
// export const IS_AW_KE_STG = true;

export const IS_AW_NL_STG = window.location.hostname === AW_STG_NL_DOMAIN;
// export const IS_AW_NL_STG = true;

export const IS_AW_KH = IS_AW_KH_PROD || IS_AW_KH_STG;
export const IS_AW_MM = IS_AW_MM_PROD || IS_AW_MM_STG;
export const IS_AW_VN = IS_AW_VN_PROD || IS_AW_VN_STG;
export const IS_PECH = IS_PECH_PROD || IS_PECH_STG;
export const IS_AW_BD = IS_AW_BD_PROD || IS_AW_BD_STG;
export const IS_AW_LA = IS_AW_LA_PROD || IS_AW_LA_STG;
export const IS_AW_KE = IS_AW_KE_PROD || IS_AW_KE_STG;
export const IS_AW_NL = IS_AW_NL_PROD || IS_AW_NL_STG;
export const IS_BITSBET = IS_BITSBET_PROD || IS_BITSBET_STG;

export const IS_KH = IS_AW_KH || IS_PECH;

export const IS_ACE_MM = IS_ACE_MM_PROD || IS_ACE_MM_STG;

export const IS_AW_TT = IS_AW_TT_PROD;
export const IS_AW_MR = IS_AW_MR_PROD;
export const IS_AW_MY = IS_AW_MY_PROD;
export const IS_AW_ID = IS_AW_ID_PROD;


export const IS_US = IS_US_PROD;

export const IS_ASIA_TT = IS_ASIA_TT_PROD;
export const IS_ASIA_MR = IS_ASIA_MR_PROD;
export const IS_ASIA_KE = IS_ASIA_KE_PROD;
export const IS_ASIA_VN = IS_ASIA_VN_PROD;
export const IS_ASIA_MY = IS_ASIA_MY_PROD;
export const IS_ASIA_ID = IS_ASIA_ID_PROD;


export const IS_ASIA_US = IS_ASIA_US_PROD;
export const IS_TGM777 = IS_TGM777_PROD;



export const IS_BIZ_TT = IS_BIZ_TT_PROD;
export const IS_BIZ_LA = IS_BIZ_LA_PROD;
export const IS_BIZ_KE = IS_BIZ_KE_PROD;
export const IS_BIZ_VN = IS_BIZ_VN_PROD;

export const IS_ASIA =  IS_ASIA_TT || IS_ASIA_MR || IS_ASIA_KE || IS_ASIA_VN || IS_ASIA_US || IS_ASIA_MY || IS_ASIA_ID || IS_TGM777;


export const IS_AGT = IS_ASIA_TT || IS_ASIA_MR || IS_ASIA_KE || IS_ASIA_VN || IS_BIZ_TT || IS_BIZ_LA || IS_BIZ_KE || IS_BIZ_VN || IS_ASIA_US || IS_ASIA_MY || IS_ASIA_ID || IS_TGM777;

export const IS_NEW_AW = IS_AW_TT || IS_AW_MR || IS_AW_KE || IS_AW_VN || IS_US || IS_AW_MY || IS_AW_ID;

// 2026/9 入座制改版生效站台（品牌 flag + 資料驅動，見 seatGroups.js）。
// 僅此四站＋本機開發走新流程；其他站台（mm/vn/bd…與凍結的 ke）維持現行行為。
// 之後某站台後端升級（getUserInfo 回 JP/Fish/Slot/Mult list），把它加進來即可。
export const IS_SEAT_ONLY = IS_AW_TT || IS_ASIA_TT || IS_US || IS_ASIA_US || IS_LOCAL;

// Trans code feature enabled for all ??.aw123.cc domains + acewin.us
export const IS_TRANS_CODE_ENABLED =
    IS_AW_KH || IS_AW_MM || IS_AW_VN || IS_AW_BD || IS_AW_LA ||
    IS_AW_KE || IS_AW_NL || IS_AW_TT || IS_AW_MR || IS_AW_MY || IS_AW_ID ||
    IS_US;

// io_mode helpers for menu visibility control
export const shouldShowDeposit = (ioMode) => [1, 3, 5, 7].includes(ioMode);
export const shouldShowWithdraw = (ioMode) => [2, 3, 6, 7].includes(ioMode);
export const shouldShowTransfer = (ioMode) => [4, 5, 6, 7].includes(ioMode);

export const isShowInstallFloatButton = !IS_AW_TT && !IS_AW_MR && !IS_ASIA_TT && !IS_NEW_AW;


export function shouldDisplayBalanceError() {

    if (IS_GW_STG
        || IS_AW_BD_STG
        || IS_AW_KH_STG
        || IS_AW_LA_STG
        || IS_AW_MM_STG
        || IS_AW_NL_STG
        || IS_AW_VN_STG
        || IS_AW_KE_STG
        || IS_PECH_STG
        || IS_BITSBET_STG
        || IS_ACE_MM
        || IS_AW_TT
        || IS_AW_MR
        || IS_AW_KE
        || IS_AW_VN
        || IS_NEW_AW) return true;

    return false;
}

export function shouldDisplayLoginButton() {
    if (IS_US) return false;
    return true;
}

export function shouldDisplayLogoutButton() {
    if (IS_US) return false;
    // 由外部帶 ukey 導入的 session（如 App WebView），登出後無法自行重新登入
    if (HAS_URL_UKEY) return false;
    return true;
}

export function shouldDisplayRegisterButton() {
    if (IS_US) return false;
    return true;
}

export function shouldPromptPWAInstall() {
    if (HAS_URL_UKEY) return false;
    return true;
}

export const TransactionLogStatusEnum = {
    WAITING: 0,
    COMPLETED: 8,
    FAILED: 2,
};

export const UserRoleEnum = {
    TRIAL: 0,
    L1: 1,
    L2: 2,
    L3: 3,
};

const MenuNameEnum = {
    HOME: 'home',
    WALLET: 'wallet',
    DEPOSIT: 'deposit',
    WITHDRAWAL: 'withdrawal',
    TRANSFER: 'transfer',
    BANKACCOUNT: 'bankaccount',
    TRANSACTION_LOG: 'transaction-log',
    BET_LOG: 'bet-log',
    AFFILIATE_LOG: 'affiliate-log',
    PROFILE: 'profile',
    // Add other menu names as needed
    NEW_USER: 'new-user',

};

// ---- 後端 menu 欄位解析 ----
// menu 值有三種模式：
//   'Y'                          → 全開，選單維持現行邏輯
//   '' / 未回傳 / 全為未知代碼    → 全關，人頭按鈕不顯示
//   'deposit;bet-log;profile'    → 篩選，選單只顯示清單內項目
// 代碼即前端既有 MenuNameEnum 值，不做對照轉換。
//
// 註：即使只給單一功能也走 filtered（不做捷徑跳頁），因為選單必定
// 額外包含 home 與 logout，直接跳頁會使這兩個入口消失。

export const MENU_ALLOWED_KEYS = [
    MenuNameEnum.DEPOSIT,
    MenuNameEnum.WITHDRAWAL,
    MenuNameEnum.TRANSFER,
    MenuNameEnum.TRANSACTION_LOG,
    MenuNameEnum.BET_LOG,
    MenuNameEnum.AFFILIATE_LOG,
    MenuNameEnum.PROFILE,
];

export const parseMenuSetting = (menu) => {
    if (menu === 'Y') return { mode: 'all', keys: [] };
    if (typeof menu !== 'string') return { mode: 'none', keys: [] };

    const keys = menu
        .split(';')
        .map((s) => s.trim())
        .filter((s) => MENU_ALLOWED_KEYS.includes(s));

    if (keys.length === 0) return { mode: 'none', keys: [] };
    return { mode: 'filtered', keys };
};

export const CommonMenuItems = {
    [MenuNameEnum.HOME]: {
        menuName: 'home',
        icon: 'home',
        path: '/',
        group: 'main',
    },
    [MenuNameEnum.WALLET]: {
        menuName: 'wallet',
        icon: 'wallet',
        path: '#',
        needLogin: true,
        group: 'history',
    },
    [MenuNameEnum.DEPOSIT]: {
        menuName: 'deposit',
        icon: 'deposit',
        path: SWITCH_ROUTES.DEPOSIT,
        needLogin: true,
        menuNamePrefix: getBankingPrefix(),
        group: 'wallet',
        textColor: '#b545ff',
    },
    [MenuNameEnum.WITHDRAWAL]: {
        menuName: 'withdrawal',
        icon: 'withdrawal',
        path: SWITCH_ROUTES.WITHDRAWAL,
        needLogin: true,
        menuNamePrefix: getBankingPrefix(),
        group: 'wallet',
        textColor: '#b545ff',
    },
    [MenuNameEnum.TRANSFER]: {
        menuName: 'transfer',
        icon: 'transfer',
        path: SWITCH_ROUTES.TRANSFER,
        needLogin: true,
        group: 'wallet',
        textColor: '#b545ff',

    },
    [MenuNameEnum.BANKACCOUNT]: {
        menuName: 'bankaccount',
        icon: 'bankaccount',
        path: SWITCH_ROUTES.BANKACCOUNT,
        needLogin: true,
        group: 'history',
        textColor: '#b545ff',

    },
    [MenuNameEnum.TRANSACTION_LOG]: {
        menuName: 'transaction-log',
        icon: 'transaction-log',
        path: SWITCH_ROUTES.TRANSATION_LOG,
        needLogin: true,
        group: 'history',
        textColor: '#fa42b7',
    },
    [MenuNameEnum.BET_LOG]: {
        menuName: 'bet-log',
        icon: 'bet-log',
        path: SWITCH_ROUTES.BET_LOG,
        needLogin: true,
        group: 'history',
        textColor: '#fa42b7',
    },
    [MenuNameEnum.AFFILIATE_LOG]: {
        menuName: 'affiliate-log',
        icon: 'affiliate-log',
        path: SWITCH_ROUTES.AFFILIATE_LOG,
        needLogin: true,
        group: 'history',
        textColor: '#fa42b7',
    },
    [MenuNameEnum.NEW_USER]: {
        menuName: 'new-user',
        icon: 'new-user',
        path: SWITCH_ROUTES.NEW_USER,
        needLogin: true,
        group: 'logout',
    },
    [MenuNameEnum.PROFILE]: {
        menuName: 'profile',
        icon: 'password',
        path: SWITCH_ROUTES.PROFILE,
        needLogin: true,
        group: 'logout',
    },
};

// Group labels for the menu
export const MenuGroups = {
    wallet: {
        label: 'Wallet',
        order: 1
    },
    history: {
        label: 'History', 
        order: 2
    },
    main: {
        label: '',
        order: 0
    },
    logout: {
        label: '',
        order: 999
    }
};

// Helper function to group menu items
export const groupMenuItems = (menuItems) => {
    const grouped = {};
    
    menuItems.forEach(item => {
        const groupKey = item.group || 'main';
        if (!grouped[groupKey]) {
            grouped[groupKey] = {
                ...MenuGroups[groupKey],
                items: []
            };
        }
        grouped[groupKey].items.push(item);
    });
    
    // Sort groups by order
    return Object.entries(grouped)
        .sort(([,a], [,b]) => a.order - b.order)
        .map(([key, group]) => ({ key, ...group }));
};

function getMenuItems() {
    if (IS_GW || IS_LOCAL) {
        const orderedKeys = Object.values(MenuNameEnum);
        const orderedMenuItemsArray = orderedKeys.map(
            (key) => CommonMenuItems[key]
        );

        return orderedMenuItemsArray;
    } else {
        const orderedKeys = Object.values(MenuNameEnum);

        // Filter out the keys for the items to be excluded
        let filteredKeys = [];

        if (IS_KH || IS_AW_VN_STG || IS_AW_MM_STG) {
            filteredKeys = orderedKeys.filter(
                (key) => ![MenuNameEnum.BANKACCOUNT].includes(key)
            );


        } else {
            filteredKeys = orderedKeys.filter(
                (key) =>
                    ![
                        MenuNameEnum.DEPOSIT,
                        MenuNameEnum.WITHDRAWAL,
                    ].includes(key)
            );
        }

        const filteredMenuItemsArray = filteredKeys.map(
            (key) => CommonMenuItems[key]
        );


        return filteredMenuItemsArray;
    }
}

export const MENU_ITEMS = getMenuItems();


export const MENU_ITEM_HOME = {
    menuName: 'home',
    icon: 'home',
    path: '/',
};


export const MENU_ITEM_LOGOUT = {
    menuName: 'logout',
    icon: 'logout',
    path: SWITCH_ROUTES.LOGOUT,
    group: 'logout',
};

export const MENU_ITEM_NEW_USER = {
    menuName: 'new-user',
    icon: 'new-user',
    path: SWITCH_ROUTES.NEW_USER,
    group: 'logout',
};

export const NO_BALANCE_ROUTES = [
    SWITCH_ROUTES.REGISTER,
    SWITCH_ROUTES.LIVE_CHAT,
    SWITCH_ROUTES.LOGOUT,
];

export const FlexDirectionConfigs = {
    FlexStart: 'flex-start',
    FlexEnd: 'flex-end',
    Center: 'center',
};

export const LOCALS = {
    cn: 'cn',
    en: 'en',
    bahasa: 'bahasa',
    vietnamese: 'vietnamese',
    khmer: 'khmer',
    myanmar: 'myanmar',
    bengali: 'bengali',
    laotian: 'laotian',
    thai: 'thai',
    nederland: 'nederland',
    indonesian: 'indonesian',
    russian: 'russian'
};

export const LOCALS_IGS_CODE_MAP = {
    cn: 'zh-cn',
    en: 'en-us',
    bahasa: 'ms-my',
    vietnamese: 'vi-vn',
    khmer: 'en-us',
    myanmar: 'my-mm',
    bengali: 'en-us',
    laotian: 'en-us',
    thai: 'th-th',
    nederland: 'en-us',
    indonesian: 'en-us',
    russian: 'ru-ru'
};

function getLanguageSelections() {

    const baseLangs = [
        { label: '简体中文', value: LOCALS.cn },
        { label: 'English', value: LOCALS.en },
    ];
    if (IS_LOCAL) {
        return [
            ...baseLangs,
            { label: 'Bahasa', value: LOCALS.bahasa },
            { label: 'ភាសាខ្មែរ', value: LOCALS.khmer },
            { label: 'မြန်မာဘာသာ', value: LOCALS.myanmar },
            { label: 'Tiếng Việt', value: LOCALS.vietnamese },
            { label: 'বাংলা', value: LOCALS.bengali },
            { label: 'ອັກສອນລາວ', value: LOCALS.laotian },
            { label: 'ภาษาไทย', value: LOCALS.thai },
            { label: 'Nederland', value: LOCALS.nederland },
            { label: 'Indonesia', value: LOCALS.indonesian },
            { label: 'Русский', value: LOCALS.russian }
        ];
    } else if (IS_US) {
        return [
            ...baseLangs,
            { label: 'မြန်မာဘာသာ', value: LOCALS.myanmar },
            { label: 'Bahasa', value: LOCALS.bahasa },
            { label: 'Indonesia', value: LOCALS.indonesian },
            { label: 'Tiếng Việt', value: LOCALS.vietnamese },
            { label: 'ภาษาไทย', value: LOCALS.thai },
            { label: 'Русский', value: LOCALS.russian }
        ];
    } else if (IS_TGM777) {
        return [
            ...baseLangs,
            { label: 'မြန်မာဘာသာ', value: LOCALS.myanmar },
            { label: 'Bahasa', value: LOCALS.bahasa },
            { label: 'Indonesia', value: LOCALS.indonesian },
            { label: 'Tiếng Việt', value: LOCALS.vietnamese },
        ];
    } else if (IS_GW || IS_AW_MY || IS_ASIA_MY) {
        return [...baseLangs, { label: 'Bahasa', value: LOCALS.bahasa }];
    } else if (IS_KH || IS_PECH) {
        return [...baseLangs, { label: 'ភាសាខ្មែរ', value: LOCALS.khmer }];
    } else if (IS_AW_MM || IS_AW_TT || IS_AW_MR || IS_ASIA_TT || IS_ASIA_MR || IS_ASIA_US) {
        return [...baseLangs, { label: 'မြန်မာဘာသာ', value: LOCALS.myanmar }];
    } else if (IS_AW_VN || IS_ASIA_VN || IS_BIZ_VN) {
        return [
            { label: 'Tiếng Việt', value: LOCALS.vietnamese },
            ...baseLangs,
        ];
    } else if (IS_AW_BD) {
        return [...baseLangs, { label: 'বাংলা', value: LOCALS.bengali }];
    } else if (IS_AW_LA) {
        return [
            ...baseLangs,
            { label: 'ອັກສອນລາວ', value: LOCALS.laotian },
            { label: 'ภาษาไทย', value: LOCALS.thai },
        ];
    } else if (IS_AW_NL) {
        return [
            ...baseLangs,
            { label: 'Nederland', value: LOCALS.nederland },
        ];
    } else if (IS_AW_ID || IS_ASIA_ID) {
        return [
            ...baseLangs,
            { label: 'Indonesia', value: LOCALS.indonesian } 
        ];
    } 


    return baseLangs;
}

export const LOCAL_SELECTIONS = getLanguageSelections();

function getMetaTitle() {
    if (IS_GW) {
        return 'GW123';
    } else if (IS_PECH) {
        return 'PECH';
    } else if (IS_AW_KH) {
        return 'AW123';
    } else if (IS_AW_MM || IS_AW_TT || IS_AW_MR || IS_ASIA_TT || IS_ASIA_MR) {
        return 'AW123';
    } else if (IS_AW_VN) {
        return 'AW123';
    } else if (IS_AW_BD) {
        return 'AW123';
    } else if (IS_AW_LA) {
        return 'AW123';
    } else if (IS_AW_KE) {
        return 'AW123';
    } else if (IS_AW_NL) {
        return 'AW123';
    } else if (IS_ACE_MM) {
        return 'AW123';
    } else if (IS_BITSBET) {
        return 'BITSBET';
    }

    return 'AW123';
}

export const META_TITLE = getMetaTitle();

function getMetaDescription() {
    if (IS_GW || IS_AW_MY || IS_ASIA_MY) {
        return 'The Best Online Game Platform Malaysia';
    } else if (IS_KH || IS_PECH) {
        return 'The Best Online Game Platform Combodia';
    } else if (IS_AW_MM || IS_AW_TT || IS_AW_MR || IS_ASIA_TT || IS_ASIA_MR || IS_BIZ_TT) {
        return 'The Best Online Game Platform Myanmar';
    } else if (IS_AW_VN || IS_ASIA_VN || IS_BIZ_VN) {
        return 'The Best Online Game Platform Vietnam';
    } else if (IS_AW_BD) {
        return 'The Best Online Game Platform in Bangladesh';
    } else if (IS_AW_LA || IS_BIZ_LA) {
        return 'The Best Online Game Platform in Laos';
    } else if (IS_AW_KE || IS_BITSBET || IS_ASIA_KE || IS_BIZ_KE) {
        return 'The Best Online Game Platform in Kenya';
    } else if (IS_AW_NL) {
        return 'The Best Online Game Platform in Nederland';
    } else if (IS_AW_ID || IS_ASIA_ID) {
        return 'The Best Online Game Platform in Indonesia';
    }

    return 'The Best Online Game Platform Malaysia';
}

export const META_DESCRIPTION = getMetaDescription();

function getMaintenanceText() {
    if (IS_GW || IS_AW_MY || IS_ASIA_MY) {
        return 'Notis Penyelenggaraan Sistem\nLaman Web Rasmi Ditangguhkan Sementara';
    } else if (IS_KH) {
        return 'សេវាកម្មថ្មីប្រកាសពិនិត្យប្រភេទប្រព័ន្ធបណ្ដាញ\nការចូលទៅកាន់គេហទំព័រក្រៅវិស្វកររបស់យើងមិនមានទេ';
    } else if (IS_AW_MM || IS_AW_TT || IS_AW_MR || IS_ASIA_TT || IS_ASIA_MR) {
        return 'စနစ်ထိန်းသိမ်းမှုသတိပေးချက်\nတရားဝင် ဝဘ်ဆိုဒ်ကို ယာယီ မရနိုင်ပါ။';
    } else if (IS_AW_VN || IS_ASIA_VN || IS_BIZ_VN) {
        return 'Thông báo bảo trì hệ thống\nTrang web chính thức tạm ngừng sử dụng';
    } else if (IS_AW_ID || IS_ASIA_ID) {
        return 'Pemberitahuan Pemeliharaan Sistem\nSitus web resmi untuk sementara tidak tersedia';
    }

    return 'Notis Penyelenggaraan Sistem\nLaman Web Rasmi Ditangguhkan Sementara';
}

export const MAINTENANCE_TEXT = getMaintenanceText();

// Device type configuration
export const DEVICE_TYPES = {
    BROWSER: 'b',
    NATIVE: 'n'
};

// Feature toggles
export const FEATURES = {
    FREE_PLAY_ENABLED: process.env.REACT_APP_FREE_PLAY_ENABLED === 'true',
    VIDEO_HELP_URL: process.env.REACT_APP_VIDEO_HELP_URL || 'https://example.com/help-video'
};

// Login error codes
export const LOGIN_ERROR_CODES = {
    BIRTHDAY_MISSING: 95,
    NAME_MISSING: 98
};

function getMetaPrefix() {
    const prefix = window.location.hostname.split('.')[0];

    return prefix + '_';
}

export const META_PREFIX = getMetaPrefix();

function getBankingPrefix() {
    if (IS_AW_KH) {
        return '';
    } else if (IS_AW_VN) {
        return '';
    } else {
        return '';
    }
}



export const BANK_METAS_INFO = {
    MOMO: {
        name: 'MOMO',
        imageUrl: '/images/payments/momo.jpg'

    },
    ZALO: {
        name: 'ZALO',
        imageUrl: '/images/payments/zalo.jpg'

    },
    ABA: {
        name: 'ABA',
        imageUrl: '/images/payments/aba.jpg'

    },
    WING: {
        name: 'WING',
        imageUrl: '/images/payments/wing.jpg',
        // imageWidth: '240px'
    },
    KBZ: {
        name: 'KBZ',
        imageUrl: '/images/payments/kbz.jpg'

    },
    WAVE: {
        name: 'WAVE',
        imageUrl: '/images/payments/wave.jpg'

    },
    AYA: {
        name: 'AYA',
        imageUrl: '/images/payments/aya.jpg'

    },
    bKash: {
        name: 'bKash',
        imageUrl: '/images/payments/bkash.jpg'

    },
    Nagad: {
        name: 'Nagad',
        imageUrl: '/images/payments/nagad.png'

    },
    BCEL: {
        name: 'BCEL',
        imageUrl: '/images/payments/bcel.jpg'

    },
    MPESA: {
        name: 'MPESA',
        imageUrl: '/images/payments/mpesa.jpg'

    },
    MYBK01: {
        name: 'MYBK01',
        imageUrl: '/images/payments/MYBK01.jpg'
    },
    MYBK02: {
        name: 'MYBK02',
        imageUrl: '/images/payments/MYBK02.png',
        imageWidth: '60px'
    },
    MYBK03: {
        name: 'MYBK03',
        imageUrl: '/images/payments/MYBK03.png',
        imageWidth: '60px'
    },
    MYBK04: {
        name: 'MYBK04',
        imageUrl: '/images/payments/MYBK04.png',
        imageWidth: '60px'
    },
    MYBK05: {
        name: 'MYBK05',
        imageUrl: '/images/payments/MYBK05.png',
        imageWidth: '60px'
    },
    GOPAY: {
        name: 'GoPay',
        imageUrl: '/images/payments/gopay.png',
        imageWidth: '60px'
    },
    U: {
        name: 'USDT',
        imageUrl: '/images/payments/USDT.png',
    },
    M: {
        name: 'MPESA',
        imageUrl: '/images/payments/MPESA.png'

    },
    C: {
        name: 'CAM',
        imageUrl: '/images/payments/CAM.png'
    },
    T: {
        name: 'TLG',
        imageUrl: '/images/payments/TLG.png'
    }

}


export function shouldLogoDisplay() {
    if (IS_NEW_AW || IS_AGT) {
        return false;
    }

    return true;
}

export function shouldSidebarAccountDisplay() {
    if (IS_NEW_AW || IS_AGT) {
        return false;
    }

    return true;
}

export function shouldQuickButtonsDisplay() {
     if (IS_NEW_AW || IS_AGT) {
        return false;
    }

    return true;
}


export function shouldDisplayInstallButtonsInSidebar() {
    if (IS_NEW_AW) {
        return true;
    }

    return false;
}
